/*
  ClientDetailsTab — "at-a-glance" personal / contact / emergency-contact
  card that lives inside a client's detail page. Editable inline so the
  therapist can correct a phone number without re-opening the intake
  form.

  Data flow
    GET  /api/clients/:email/profile   → merged view (intake + overrides)
    PUT  /api/clients/:email/profile   → persist overrides

  We track which fields the operator hand-edited so we can render a
  "differs from intake form" hint next to them.
*/
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCanonicalClient } from "@/lib/canonicalClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, RotateCcw, PencilLine } from "lucide-react";
import { toast } from "sonner";
import ReferralSourceCard from "@/components/ReferralSourceCard";

const TITLE_OPTIONS = [
  "Mr", "Mrs", "Ms", "Miss", "Mx", "Dr", "Prof", "Rev", "Sir", "Dame",
];

const SECTIONS = [
  {
    title: "Personal",
    fields: [
      // Salutation / honorific used on GP letter Re: blocks and body
      // copy references ("Mrs Bianco"). Optional — blank means the
      // downstream renderer falls back to the client's full name.
      { key: "title", label: "Title", options: TITLE_OPTIONS },
      // Splitting the name into three fields lets the operator record
      // middle names accurately (needed for WorkSafe / Medicare) and
      // gives downstream forms clean placeholders. The backend keeps
      // the legacy `full_name` in sync automatically.
      { key: "first_name", label: "First name" },
      { key: "middle_name", label: "Middle name (optional)" },
      { key: "last_name", label: "Surname" },
      { key: "dob", label: "Date of birth", type: "date" },
      { key: "gender", label: "Gender", optionsKey: "gender" },
      { key: "pronouns", label: "Pronouns", optionsKey: "pronouns" },
    ],
  },
  {
    title: "Contact",
    fields: [
      { key: "mobile", label: "Mobile", type: "tel" },
      { key: "email", label: "Email", type: "email" },
      { key: "address", label: "Address", multiline: true, span: 2 },
    ],
  },
  {
    title: "Emergency contact",
    fields: [
      { key: "emergency_contact_name", label: "Name" },
      { key: "emergency_contact_relationship", label: "Relationship" },
      { key: "emergency_contact_phone", label: "Phone", type: "tel", span: 2 },
    ],
  },
];

// Fallback option lists — mirrors backend defaults in
// routes/profile_options.py so a first-load render is instant even
// before the fetch resolves.
const DEFAULT_GENDER = [
  "Female", "Male", "Non-binary",
  "Prefer to self-describe", "Prefer not to say",
];
const DEFAULT_PRONOUNS = [
  "she/her", "he/him", "they/them", "she/they", "he/they",
  "Prefer to self-describe", "Prefer not to say",
];

// Order matters: matches SECTIONS above so buildDiff walks in stable order.
const FIELD_KEYS = SECTIONS.flatMap((s) => s.fields.map((f) => f.key));

// Self-describe prefix — the ONLY way a "Prefer to self-describe" option
// can carry the operator's exact wording without needing a second DB
// column. We store the final value as ``"Prefer to self-describe: <text>"``
// so downstream readers (Letters, PDF exports) get a natural sentence.
const SELF_DESCRIBE_LABEL = "Prefer to self-describe";
const SELF_DESCRIBE_PREFIX = `${SELF_DESCRIBE_LABEL}: `;

function isSelfDescribeValue(v) {
  if (typeof v !== "string") return false;
  return v === SELF_DESCRIBE_LABEL || v.startsWith(SELF_DESCRIBE_PREFIX);
}
function selfDescribeText(v) {
  if (typeof v !== "string") return "";
  return v.startsWith(SELF_DESCRIBE_PREFIX)
    ? v.slice(SELF_DESCRIBE_PREFIX.length)
    : "";
}
// Case-insensitive lookup — intake forms often store "She/Her" while
// the option list uses lower-case "she/her". This lets the dropdown
// pre-select the matching option instead of showing a spurious
// "(from intake)" placeholder.
function findKnownOption(options, value) {
  if (!value) return null;
  if (isSelfDescribeValue(value)) return SELF_DESCRIBE_LABEL;
  const lower = String(value).trim().toLowerCase();
  return options.find((o) => o.toLowerCase() === lower) || null;
}

export default function ClientDetailsTab({ email, onSaved }) {
  // Phase-2 canonical scope — when the workspace is at
  // ``/clients/<email>?as=<cid>`` we send ``?canonical_client_id=<cid>``
  // so this tab reads + writes to the per-canonical profile document.
  // Without the param the endpoint keeps its legacy email-keyed
  // behaviour (single-sibling emails see no change).
  const canonical = useCanonicalClient();
  const canonicalId = canonical?.clientId || "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ profile: {}, from_intake: {}, edited_fields: [] });
  const [form, setForm] = useState({});
  const [dirty, setDirty] = useState(false);
  // Clinic-editable option lists (Settings → Client fields). Falls
  // back to the built-in defaults so the dropdowns render before the
  // fetch resolves.
  const [profileOptions, setProfileOptions] = useState({
    gender: DEFAULT_GENDER, pronouns: DEFAULT_PRONOUNS,
  });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const [profileResp, optsResp] = await Promise.all([
        api.get(`/clients/${encodeURIComponent(email)}/profile`, {
          params: canonicalId ? { canonical_client_id: canonicalId } : undefined,
        }),
        api.get("/settings/profile-options").catch(() => null),
      ]);
      setData(profileResp.data);
      setForm(profileResp.data.profile || {});
      setDirty(false);
      if (optsResp?.data) {
        setProfileOptions({
          gender: optsResp.data.gender || DEFAULT_GENDER,
          pronouns: optsResp.data.pronouns || DEFAULT_PRONOUNS,
        });
      }
    } catch (e) {
      toast.error("Could not load profile", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [email, canonicalId]);

  useEffect(() => { load(); }, [load]);

  function change(k) {
    return (e) => {
      const v = e.target.value;
      setForm((f) => ({ ...f, [k]: v }));
      setDirty(true);
    };
  }

  async function save() {
    setSaving(true);
    try {
      // Detect an email change up front. Email is the client's primary
      // key across the whole app (URL, letters, notes, appointments,
      // SbN links, exercise programs, ...), so a change is a full-app
      // migration — NOT a plain PUT — and we route through the
      // dedicated /change-email endpoint that walks every collection
      // + writes a reversible audit row. Everything else in the form
      // still goes via the standard PUT that follows.
      const oldEmail = (email || "").trim().toLowerCase();
      const newEmail = (form.email || "").trim().toLowerCase();
      // Email-change is a full-email migration and would drag any
      // sibling on this email along for the ride. When the workspace
      // is scoped to a specific canonical (Judy on a shared email),
      // silently skip the change-email path — the operator can still
      // rename the person via the Rename dialog, and needs to open
      // the unscoped view for a true email migration.
      if (!canonicalId && newEmail && newEmail !== oldEmail) {
        const confirmed = window.confirm(
          `Change this client's email address?\n\n` +
          `FROM: ${oldEmail}\n` +
          `TO:   ${newEmail}\n\n` +
          `This will migrate ALL of the client's records — letters, ` +
          `SOAP notes, appointments, tasks, medical alerts, exercise ` +
          `programs, SbN links, and every other reference — onto the ` +
          `new email. An audit row is written so it's reversible.\n\n` +
          `If the new email is a DIFFERENT person, cancel and use ` +
          `Merge Clients instead.\n\nProceed?`,
        );
        if (!confirmed) {
          setSaving(false);
          return;
        }
        try {
          const r = await api.post(
            `/clients/${encodeURIComponent(oldEmail)}/change-email`,
            { new_email: newEmail },
          );
          const moved = r.data.total_moved ?? 0;
          toast.success(
            `Email changed — ${moved} record${moved === 1 ? "" : "s"} moved`,
          );
          // Route to the new URL. The parent page reloads its profile
          // from scratch so every downstream tab (SOAP, letters, etc.)
          // targets the new email.
          navigate(`/clients/${encodeURIComponent(newEmail)}`, { replace: true });
          return;
        } catch (e) {
          toast.error("Email change failed", {
            description: e?.response?.data?.detail || e.message,
          });
          setSaving(false);
          return;
        }
      }

      // Standard non-email-change save.
      // Only send fields the therapist actually edited or that already
      // have overrides — avoids over-writing intake values with blanks.
      const payload = {};
      for (const k of FIELD_KEYS) {
        payload[k] = (form[k] ?? "").toString();
      }
      // Multi-value: alternate contact emails. Empty array clears
      // the override; the backend normalises (strip, lowercase,
      // dedupe, drop primary).
      payload.additional_emails = Array.isArray(form.additional_emails)
        ? form.additional_emails
        : [];
      const r = await api.put(`/clients/${encodeURIComponent(email)}/profile`, payload, {
        params: canonicalId ? { canonical_client_id: canonicalId } : undefined,
      });
      setData(r.data);
      setForm(r.data.profile || {});
      setDirty(false);
      toast.success("Client details saved");
      // Bubble the fresh profile up to the parent so the "Client Name"
      // header at the top of the page (and the global ``activeClient``
      // used by Letters, SbN, etc.) updates immediately — without this
      // callback the header stays stuck on whatever name was in
      // ``/intake/clients`` when the page loaded.
      onSaved?.(r.data.profile || {});
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setForm(data.profile || {});
    setDirty(false);
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading details…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-details-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-zinc-600">
          Snapshot pulled from the client&apos;s intake form. Edits here override the intake
          without re-submitting it.
          {data.updated_at && (
            <span className="ml-1 text-zinc-500">
              Last edited {new Date(data.updated_at).toLocaleDateString()}.
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button
              variant="outline"
              size="sm"
              onClick={reset}
              disabled={saving}
              data-testid="client-details-reset"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Discard
            </Button>
          )}
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="client-details-save"
          >
            {saving
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
              : <><Save className="h-3.5 w-3.5 mr-1" /> Save changes</>}
          </Button>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <Card
          key={section.title}
          className="p-4 border-zinc-200"
          data-testid={`client-details-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium mb-2">
            {section.title}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {section.fields.map((f) => {
              const isEdited = data.edited_fields?.includes(f.key);
              return (
                <div
                  key={f.key}
                  className={f.span === 2 ? "sm:col-span-2" : undefined}
                >
                  <Label className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                    {f.label}
                    {isEdited && (
                      <span
                        className="inline-flex items-center gap-0.5 text-2xs normal-case tracking-normal text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                        title={`Intake value: ${data.from_intake?.[f.key] || "(empty)"}`}
                      >
                        <PencilLine className="h-2.5 w-2.5" /> edited
                      </span>
                    )}
                  </Label>
                  {f.multiline ? (
                    <textarea
                      value={form[f.key] || ""}
                      onChange={change(f.key)}
                      rows={2}
                      className="mt-1 w-full rounded-md border border-zinc-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-400"
                      data-testid={`client-details-input-${f.key}`}
                    />
                  ) : f.options ? (
                    /*
                      Static-list dropdown (e.g. Title — Mr / Mrs / …).
                      Unlike ``optionsKey`` this doesn't pull from the
                      profile-options endpoint since a handful of
                      honorifics don't warrant a clinic-editable list.
                      An "Other" bucket keeps unusual titles from
                      being stripped: any current value that doesn't
                      match the shortlist is preserved as a "(custom)"
                      option so the operator can still see it and
                      commit as-is.
                    */
                    (() => {
                      const raw = form[f.key] || "";
                      const known = f.options.find(
                        (o) => o.toLowerCase() === raw.toLowerCase(),
                      );
                      return (
                        <select
                          value={known || (raw ? "__custom__" : "")}
                          onChange={(e) => {
                            const chosen = e.target.value;
                            if (chosen === "__custom__") return;
                            change(f.key)({ target: { value: chosen } });
                          }}
                          className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-400"
                          data-testid={`client-details-input-${f.key}`}
                        >
                          <option value="">— none —</option>
                          {f.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                          {raw && !known && (
                            <option value="__custom__">{raw} (custom)</option>
                          )}
                        </select>
                      );
                    })()
                  ) : f.optionsKey ? (
                    /*
                      Dropdown-style field driven by the clinic-editable
                      option list (Settings → Client fields). We match
                      the current value case-insensitively so an intake
                      like "She/Her" pre-selects the "she/her" option
                      instead of falling through to the "(from intake)"
                      escape hatch.

                      "Prefer to self-describe" reveals a small
                      companion input; the final stored value is
                      ``"Prefer to self-describe: <text>"`` so the
                      exact wording travels with the record without
                      needing a second DB column.
                    */
                    (() => {
                      const opts = profileOptions[f.optionsKey] || [];
                      const raw = form[f.key] || "";
                      const known = findKnownOption(opts, raw);
                      const selectedIsSelfDescribe = known === SELF_DESCRIBE_LABEL;
                      return (
                        <>
                          <select
                            value={known || (raw ? "__legacy__" : "")}
                            onChange={(e) => {
                              const chosen = e.target.value;
                              if (chosen === "__legacy__") return;  // no-op guard
                              if (chosen === SELF_DESCRIBE_LABEL) {
                                // Preserve any existing self-described
                                // text when the operator re-picks the
                                // option; store the bare label when
                                // there's nothing to describe yet so
                                // the dropdown pre-selects on reload.
                                const existing = selfDescribeText(raw);
                                setForm((fr) => ({
                                  ...fr,
                                  [f.key]: existing
                                    ? `${SELF_DESCRIBE_PREFIX}${existing}`
                                    : SELF_DESCRIBE_LABEL,
                                }));
                                setDirty(true);
                                return;
                              }
                              change(f.key)({ target: { value: chosen } });
                            }}
                            className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-400"
                            data-testid={`client-details-input-${f.key}`}
                          >
                            <option value="">— select —</option>
                            {opts.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                            {raw && !known && (
                              <option value="__legacy__">{raw} (from intake)</option>
                            )}
                          </select>
                          {selectedIsSelfDescribe && (
                            <Input
                              type="text"
                              value={selfDescribeText(raw)}
                              onChange={(e) => {
                                const txt = e.target.value;
                                setForm((fr) => ({
                                  ...fr,
                                  [f.key]: txt
                                    ? `${SELF_DESCRIBE_PREFIX}${txt}`
                                    : SELF_DESCRIBE_LABEL,
                                }));
                                setDirty(true);
                              }}
                              placeholder="e.g. Genderfluid, Two-spirit, xe/xir…"
                              className="border-zinc-300 mt-1 text-sm"
                              data-testid={`client-details-selfdesc-${f.key}`}
                            />
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <Input
                      type={f.type || "text"}
                      value={form[f.key] || ""}
                      onChange={change(f.key)}
                      className="border-zinc-300 mt-1"
                      data-testid={`client-details-input-${f.key}`}
                    />
                  )}
                </div>
              );
            })}
            {section.title === "Contact" && (
              <AlternateEmailsField
                value={form.additional_emails || []}
                onChange={(next) => {
                  setForm((f) => ({ ...f, additional_emails: next }));
                  setDirty(true);
                }}
                primaryEmail={email}
              />
            )}
          </div>
        </Card>
      ))}
      <ReferralSourceCard email={email} />
    </div>
  );
}


/**
 * Small chip-style multi-value input for alternate contact emails.
 *
 * The Client's primary email is their record ID across the whole app,
 * but real life sends messages to more than one address — both parents
 * of a child, an adult's work + personal inboxes, an executor cc'd on
 * a compensable-claim letter. Values entered here are CC'd on every
 * outbound letter and match-boosted in Client search so any of the
 * aliases lands on the same client.
 *
 * Input UX:
 *   * Type an address → press Enter or comma → chip is added
 *   * Click × on a chip to remove it
 *   * Duplicates and the primary email itself are silently ignored
 *   * Invalid-shape entries (no ``@``) are flagged red inline
 */
function AlternateEmailsField({ value, onChange, primaryEmail }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const emails = Array.isArray(value) ? value : [];

  const commit = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setError(`"${v}" is not a valid email address.`);
      return;
    }
    if (v === (primaryEmail || "").toLowerCase()) {
      setError("This is already the primary email.");
      return;
    }
    if (emails.includes(v)) {
      setError("Already in the list.");
      return;
    }
    onChange([...emails, v]);
    setDraft("");
    setError("");
  };

  return (
    <div className="sm:col-span-2" data-testid="alternate-emails-field">
      <Label className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        Alternate emails
        <span className="text-2xs normal-case tracking-normal text-zinc-400">
          — CC'd on every outbound letter, matched in client search
        </span>
      </Label>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-md border border-zinc-300 bg-white p-1.5 focus-within:ring-2 focus-within:ring-zinc-400">
        {emails.map((em) => (
          <span
            key={em}
            className="inline-flex items-center gap-1 rounded-full bg-zinc-100 text-zinc-800 text-xs px-2 py-0.5 border border-zinc-200"
            data-testid={`alternate-email-chip-${em}`}
          >
            {em}
            <button
              type="button"
              onClick={() => onChange(emails.filter((x) => x !== em))}
              className="text-zinc-500 hover:text-red-700 leading-none"
              aria-label={`Remove ${em}`}
              data-testid={`alternate-email-remove-${em}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="email"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(""); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Backspace" && !draft && emails.length) {
              onChange(emails.slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={emails.length ? "Add another…" : "e.g. parent2@example.com"}
          className="flex-1 min-w-[140px] text-sm outline-none bg-transparent"
          data-testid="alternate-email-input"
        />
      </div>
      {error && (
        <div className="mt-1 text-2xs text-red-700"
             data-testid="alternate-email-error">
          {error}
        </div>
      )}
    </div>
  );
}
